<script setup>
  import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

  const props = defineProps({
    items: {
      type: Array,
      required: true,
    },
    showNavigation: {
      type: Boolean,
      default: true,
    },
    autoPlay: {
      type: Boolean,
      default: false,
    },
    autoPlayInterval: {
      type: Number,
      default: 200,
    },
  });

  const emit = defineEmits(['change']);

  const sliderRef = ref(null);
  const currentIndex = ref(0);
  const isDragging = ref(false);
  const startX = ref(0);
  const startY = ref(0);
  const currentTranslate = ref(0);
  const prevTranslate = ref(0);
  const autoPlayTimer = ref(null);
  const dragDirection = ref(null);

  const canGoLeft = computed(() => currentIndex.value > 0);
  const canGoRight = computed(
    () => currentIndex.value < props.items.length - 1,
  );

  const translateX = computed(() => {
    const value = isDragging.value
      ? currentTranslate.value
      : -currentIndex.value * 100;
    return `translate3d(${value}%, 0, 0)`;
  });

  const goToPrevious = () => {
    if (canGoLeft.value) {
      currentIndex.value--;
      emit('change', currentIndex.value);
      resetAutoPlayTimer();
    }
  };

  const goToNext = () => {
    if (canGoRight.value) {
      currentIndex.value++;
      emit('change', currentIndex.value);
      resetAutoPlayTimer();
    }
  };

  const startAutoPlay = () => {
    if (!props.autoPlay || props.items.length <= 1) return;

    stopAutoPlay();

    autoPlayTimer.value = setInterval(() => {
      if (currentIndex.value < props.items.length - 1) {
        currentIndex.value++;
      } else {
        currentIndex.value = 0;
      }
      emit('change', currentIndex.value);
    }, props.autoPlayInterval);
  };

  const stopAutoPlay = () => {
    if (autoPlayTimer.value) {
      clearInterval(autoPlayTimer.value);
      autoPlayTimer.value = null;
    }
  };

  const resetAutoPlayTimer = () => {
    if (!props.autoPlay) return;
    stopAutoPlay();
    startAutoPlay();
  };

  const getPositionX = event => {
    return event.type.includes('mouse')
      ? event.pageX
      : event.touches[0].clientX;
  };

  const getPositionY = event => {
    return event.type.includes('mouse')
      ? event.pageY
      : event.touches[0].clientY;
  };

  const dragStart = event => {
    isDragging.value = true;
    startX.value = getPositionX(event);
    startY.value = getPositionY(event);
    dragDirection.value = null;
    sliderRef.value.style.cursor = 'grabbing';
  };

  const dragMove = event => {
    if (!isDragging.value) return;

    const currentX = getPositionX(event);
    const currentY = getPositionY(event);
    const diffX = Math.abs(currentX - startX.value);
    const diffY = Math.abs(currentY - startY.value);

    if (dragDirection.value === null && (diffX > 5 || diffY > 5)) {
      dragDirection.value = diffX > diffY ? 'horizontal' : 'vertical';
    }

    if (dragDirection.value === 'vertical') {
      isDragging.value = false;
      return;
    }

    if (dragDirection.value === 'horizontal') {
      event.preventDefault();

      const diff = currentX - startX.value;
      const containerWidth = sliderRef.value.offsetWidth;

      const translatePercent = (diff / containerWidth) * 100;
      currentTranslate.value = prevTranslate.value + translatePercent;
    }
  };

  const dragEnd = () => {
    if (!isDragging.value) return;

    isDragging.value = false;

    // Only process horizontal drags
    if (dragDirection.value === 'horizontal') {
      const movedBy = currentTranslate.value - prevTranslate.value;

      if (movedBy < -15 && canGoRight.value) {
        currentIndex.value++;
        emit('change', currentIndex.value);
      } else if (movedBy > 15 && canGoLeft.value) {
        currentIndex.value--;
        emit('change', currentIndex.value);
      }

      resetAutoPlayTimer();
    }

    currentTranslate.value = -currentIndex.value * 100;
    prevTranslate.value = -currentIndex.value * 100;
    dragDirection.value = null;

    if (sliderRef.value) {
      sliderRef.value.style.cursor = 'grab';
    }
  };

  const updateTranslate = () => {
    prevTranslate.value = -currentIndex.value * 100;
    currentTranslate.value = -currentIndex.value * 100;
  };

  onMounted(() => {
    if (sliderRef.value) {
      sliderRef.value.addEventListener('mousedown', dragStart);
      sliderRef.value.addEventListener('mousemove', dragMove);
      sliderRef.value.addEventListener('mouseup', dragEnd);
      sliderRef.value.addEventListener('mouseleave', dragEnd);

      sliderRef.value.addEventListener('touchstart', dragStart, {
        passive: false,
      });
      sliderRef.value.addEventListener('touchmove', dragMove, {
        passive: false,
      });
      sliderRef.value.addEventListener('touchend', dragEnd);
    }

    startAutoPlay();
  });

  onBeforeUnmount(() => {
    stopAutoPlay();

    if (sliderRef.value) {
      sliderRef.value.removeEventListener('mousedown', dragStart);
      sliderRef.value.removeEventListener('mousemove', dragMove);
      sliderRef.value.removeEventListener('mouseup', dragEnd);
      sliderRef.value.removeEventListener('mouseleave', dragEnd);

      sliderRef.value.removeEventListener('touchstart', dragStart);
      sliderRef.value.removeEventListener('touchmove', dragMove);
      sliderRef.value.removeEventListener('touchend', dragEnd);
    }
  });

  defineExpose({
    currentIndex,
    goToNext,
    goToPrevious,
    startAutoPlay,
    stopAutoPlay,
    resetAutoPlayTimer,
  });
</script>

<template>
  <div class="review-slider">
    <button
      v-if="showNavigation"
      class="nav-arrow nav-arrow--left desktop-only"
      :class="{ 'nav-arrow--hidden': !canGoLeft }"
      @click="goToPrevious"
      aria-label="Anterior"
      :disabled="!canGoLeft"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
          d="M15 18L9 12L15 6"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <div class="slider-container" ref="sliderRef">
      <div
        class="slider-track"
        :class="{ dragging: isDragging }"
        :style="{ transform: translateX }"
      >
        <div
          v-for="(item, index) in items"
          :key="item.id || index"
          class="slider-item"
        >
          <slot name="item" :item="item" :index="index" />
        </div>
      </div>
    </div>

    <button
      v-if="showNavigation"
      class="nav-arrow nav-arrow--right desktop-only"
      :class="{ 'nav-arrow--hidden': !canGoRight }"
      @click="goToNext"
      aria-label="Siguiente"
      :disabled="!canGoRight"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
          d="M9 18L15 12L9 6"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <div v-if="showNavigation && items.length > 1" class="mobile-navigation">
      <button
        class="nav-button"
        :disabled="!canGoLeft"
        @click="goToPrevious"
        aria-label="Anterior"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M15 18L9 12L15 6"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <button
        class="nav-button"
        :disabled="!canGoRight"
        @click="goToNext"
        aria-label="Siguiente"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M9 18L15 12L9 6"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
  @import '~assets/styles/index.scss';

  .review-slider {
    position: relative;
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;

    @media (max-width: $screen-xs-max) {
      flex-direction: column;
      gap: 0;
    }

    @media (max-width: $screen-sm-max) {
      gap: 0;
    }
  }

  .slider-container {
    flex: 1 1 auto;
    min-width: 0;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    cursor: grab;
    user-select: none;
    box-sizing: border-box;

    &:active {
      cursor: grabbing;
    }

    @media (max-width: $screen-xs-max) {
      width: 100%;
    }
  }

  .slider-track {
    display: flex;
    width: 100%;
    gap: 20px;
    transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    will-change: transform;
    box-sizing: border-box;

    &.dragging {
      transition: none;
    }

    @media (max-width: $screen-sm-max) {
      gap: 0;
    }

    @media (max-width: $screen-xs-max) {
      gap: 0;
    }
  }

  .slider-item {
    flex: 0 0 calc(100% - 20px);
    min-width: 0;
    width: calc(100% - 20px);
    max-width: calc(100% - 20px);
    display: flex;
    justify-content: center;
    box-sizing: border-box;
    padding: 0;

    @media (max-width: $screen-sm-max) {
      flex: 0 0 100%;
      width: 100%;
      max-width: 100%;
      justify-content: flex-start;
    }

    @media (max-width: $screen-xs-max) {
      flex: 0 0 100%;
      width: 100%;
      max-width: 100%;
      padding: 0;
      justify-content: flex-start;
    }
  }

  .nav-arrow {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: white;
    border: 1px solid #e0e0e0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    flex-shrink: 0;

    &:hover {
      background: #f5f5f5;
      transform: scale(1.08);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &:active {
      transform: scale(0.96);
      transition-duration: 0.1s;
    }

    svg {
      color: #333;
      transition: transform 0.2s ease;
    }

    &:hover svg {
      transform: translateX(0);
    }

    &--hidden {
      visibility: hidden;
      pointer-events: none;
    }
  }

  .desktop-only {
    display: flex;

    @media (max-width: $screen-xs-max) {
      display: none;
    }
  }

  .mobile-navigation {
    display: none;
    justify-content: flex-end;
    align-self: flex-end;
    gap: 12px;
    @media (max-width: $screen-xs-max) {
      display: flex;
      position: absolute;
      bottom: 0;
      right: 0;
    }
  }

  .nav-button {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: white;
    border: 1px solid #e0e0e0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    &:not(:disabled):active {
      transform: scale(0.92);
      transition-duration: 0.1s;
    }

    svg {
      color: #333;
      transition: color 0.2s ease;
    }

    &:disabled svg {
      color: #999;
    }
  }
</style>
